import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tabs } from "antd";
import CryptoJS from "crypto-js";
import Request from "../../../helpers/request.service";
import Auth from "../../../helpers/auth.service";
import Notification from "../../../helpers/Notification";
import { getFirstPermittedUrl } from "../../../helpers/helper";
import { authUrl } from "../../../constants/apiBaseUrl";
import { useAuth } from "../../../lib/auth";
import LoginForm from "./LoginForm";
import LoginOtp from "./LoginOtp";

/**
 * Login shell — orchestrates the LoginForm against POST /auth/login and,
 * when the backend flag `otpLoginEnabled` (GET /auth/config) is on, an
 * additive "Email OTP" tab (username + emailed code, no password).
 *
 * Both methods converge on finishLogin(payload): token persistence +
 * store-routing (0/1/>1 stores) + server-driven permission bootstrap — so the
 * post-login experience is identical.
 *
 * Multi-store routing (BRD §1.1.4) — uniform across roles, including
 * SUPER_ADMIN (who receives the full active-store list to pick from):
 *   - 0 active stores → server returns AUTH_028 (handled inline by the form).
 *   - 1 active store → POST /auth/active-store + dashboard.
 *   - >1 active stores → /store-selection card screen.
 */
export default function Login() {
  const [submitting, setSubmitting] = useState(false);
  const [errorState, setErrorState] = useState(null);
  // Login-method kill-switches (GET /auth/config). Prod is OTP-only:
  // otpLoginEnabled=true + passwordLoginEnabled=false → password tab hidden.
  const [otpEnabled, setOtpEnabled] = useState(false);
  const [passwordEnabled, setPasswordEnabled] = useState(true);
  const navigate = useNavigate();
  const { loginWithTokens, loadPermissions, setActiveStoreId } = useAuth();

  useEffect(() => {
    let active = true;
    Request.get(authUrl.auth_config)
      .then((res) => {
        if (!active) return;
        const cfg = res?.data?.data || {};
        setOtpEnabled(Boolean(cfg.otpLoginEnabled));
        // Absent (older backend) → treat password as enabled (safe default).
        setPasswordEnabled(cfg.passwordLoginEnabled !== false);
      })
      .catch(() => {
        // Config unavailable → default to password-only (safe).
        if (active) {
          setOtpEnabled(false);
          setPasswordEnabled(true);
        }
      });
    return () => {
      active = false;
    };
  }, []);

  // Shared post-authentication path — both tabs converge here.
  const finishLogin = useCallback(
    async (payload) => {
      if (!payload?.accessToken || !payload?.refreshToken) {
        setErrorState({ code: "GEN_001", message: "Unexpected response from server." });
        return;
      }
      const stores = Array.isArray(payload.assignedStores) ? payload.assignedStores : [];

      loginWithTokens({
        accessToken: payload.accessToken,
        refreshToken: payload.refreshToken,
        user: {
          id: payload.userId,
          email: payload.email,
          name: payload.fullName,
          role: payload.roleName,
          roleId: payload.roleId,
        },
        assignedStores: stores,
      });

      if (payload.requiresStoreSelection || stores.length > 1) {
        navigate("/store-selection", { replace: true });
        return;
      }

      if (stores.length === 1) {
        const onlyStore = stores[0];
        // Set the activeStoreId before the dashboard request fires so the
        // axios interceptor injects X-Store-Id on the very first call.
        Auth.activeStoreId = onlyStore.storeId;
        try {
          await Request.postJson(authUrl.active_store, { storeId: onlyStore.storeId });
        } catch {
          Auth.activeStoreId = null;
        }
        setActiveStoreId(Auth.activeStoreId);
        await loadPermissions();
        navigate(getFirstPermittedUrl(), { replace: true });
        return;
      }

      // Defensive: SUPER_ADMIN (no stores) loads permissions without an active
      // store (bypasses the store filter) and lands on the computed landing.
      await loadPermissions();
      navigate(getFirstPermittedUrl(), { replace: true });
    },
    [loginWithTokens, loadPermissions, navigate, setActiveStoreId],
  );

  const submit = useCallback(
    async ({ username, password }) => {
      setSubmitting(true);
      setErrorState(null);
      try {
        const passwordHash = CryptoJS.SHA512(password).toString(CryptoJS.enc.Hex);
        const res = await Request.postJson(authUrl.login_password, {
          username,
          password: passwordHash,
        });
        const payload = res?.data?.data;
        await finishLogin(payload);
      } catch (err) {
        const data = err?.response?.data;
        const payload = data?.data || {};
        const message = data?.message || "Sign-in failed. Please try again.";
        // Device-Bound Access (mTLS) WHERE-gate block → 403 with deviceBlocked.
        // Render an inline panel instead of the generic failure toast.
        const deviceBlocked = payload.deviceBlocked === true;
        setErrorState({
          message,
          deviceBlocked,
          attemptsRemaining: payload.attemptsRemaining,
          lockedUntil: payload.lockedUntil || null,
        });
        if (!deviceBlocked) Notification.error(message);
      } finally {
        setSubmitting(false);
      }
    },
    [finishLogin],
  );

  const clearError = useCallback(() => {
    if (errorState) setErrorState(null);
  }, [errorState]);

  const passwordTab = (
    <LoginForm
      onSubmit={submit}
      submitting={submitting}
      errorState={errorState}
      onChange={clearError}
    />
  );
  const otpTab = <LoginOtp onLoggedIn={finishLogin} />;

  // Render: both on → tabs; only one method on → that method bare. Password
  // stays the fallback if config somehow reports everything off.
  let body;
  if (passwordEnabled && otpEnabled) {
    body = (
      <Tabs
        className="mt-4"
        centered
        items={[
          { key: "password", label: "Password", children: passwordTab },
          { key: "otp", label: "Email OTP", children: otpTab },
        ]}
      />
    );
  } else if (!passwordEnabled && otpEnabled) {
    body = <div className="mt-4">{otpTab}</div>;
  } else {
    body = passwordTab;
  }

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-4">
      <div className="w-full max-w-[460px] bg-milk border border-border rounded-2xl p-8">
        <div className="text-center">
          <div className="font-display text-4xl text-cocoa">
            <span className="text-honey">✿</span> Sulsa <span className="text-honey">✿</span>
          </div>
          <div className="text-honey italic text-sm mt-1">Woven with love</div>
        </div>
        {body}
      </div>
    </div>
  );
}
